/* A price for a company whose exchange is shut.
 *
 * MEXC lists perpetual contracts on about 290 individual companies, and they
 * trade around the clock. Each one carries an `indexPrice` — the exchange's own
 * reference to what the share is worth, as against the `fairPrice` the contract
 * itself changes hands at. Measured against the equity mark when both are
 * fresh, the two agree to well under a percent; the contract's own premium over
 * its index runs about a tenth of one.
 *
 * So when New York is shut, this is a real reading of the same company from a
 * venue that is open, and the book already fetches the MEXC board for the
 * leveraged positions. It costs one extra symbol in a request that is being
 * made anyway.
 *
 * Three limits on it, and they are the whole reason this is its own file:
 *
 * It is the **index**, never the contract's traded price. The contract is a
 * different instrument with its own supply and demand; its index is MEXC's
 * answer to "what is the share worth", which is the question being asked.
 *
 * It is used **only while the exchange is fully shut**. During any session —
 * regular, pre or post — the listing itself is quoting and that is the better
 * source by definition.
 *
 * It **never reaches the record.** The value timeline is written from
 * regular-hours marks, which is why `effectiveMark` reports this as an extended
 * print rather than as the price: the screen shows it, the history does not.
 * Same treatment the app already gives a pre-market print, for the same reason
 * — a price worth showing is not automatically a price worth writing down. */

import type { PerpMark, Quote } from '../types';

/** MEXC's contract for a listed company. `MU` on the exchange screen is
    `MUSTOCK_USDT` on the API — the same suffix trap §3.2 warns about for Yahoo
    symbols, and the reason `Perp.symbol` exists apart from `Perp.display`. */
export function stockPerpSymbol(ticker: string): string {
  return `${ticker.trim().toUpperCase()}STOCK_USDT`;
}

export interface OffHoursMark {
  /** MEXC's reference price for the share, in USDT. */
  price: number;
  /** The contract it came from, so the screen can name its source. */
  symbol: string;
}

/** What the board can say about the companies held. Tickers with no contract
    are simply absent — most listings have none, and that is not a failure. */
export function offHoursMarks(
  tickers: Iterable<string>,
  perpMarks: PerpMark[],
): Map<string, OffHoursMark> {
  const bySymbol = new Map(perpMarks.map((m) => [m.symbol, m]));
  const out = new Map<string, OffHoursMark>();

  for (const ticker of tickers) {
    const symbol = stockPerpSymbol(ticker);
    const mark = bySymbol.get(symbol);
    // A contract with no index is one the board answered for but cannot price.
    if (!mark || !(mark.indexPrice > 0)) continue;
    out.set(ticker.trim().toUpperCase(), { price: mark.indexPrice, symbol });
  }
  return out;
}

/** Whether this quote should fall back to the contract's index.
 *
 * Only with the listing fully shut, and only for a listing priced in dollars —
 * the index is quoted in USDT, and putting it against a Warsaw or Hong Kong
 * listing would be comparing two currencies and calling the difference a price
 * move. */
export function usableOffHours(q: Quote, state: string): boolean {
  if (state !== 'CLOSED') return false;
  const currency = (q.currency ?? 'USD').toUpperCase();
  return currency === 'USD' || currency === 'USDT';
}
